import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { UPDATE_CMA_REPORT_INCLUDE_AGENCY_DESCRIPTION } from '@modules/reports/cma-reports/cmaReportsQueries';
import { useLocale } from '@shared/hooks/locale';

type StepAgencyProfileSettingsProps = {
  includeAgencyDescription: boolean;
};

const StepAgencyProfileSettings = ({
  includeAgencyDescription,
}: StepAgencyProfileSettingsProps) => {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data: cmaReportData } = useReportContext();

  const [updateIncludeAgencyDescription, { loading: loadingUpdate }] =
    useMutation(UPDATE_CMA_REPORT_INCLUDE_AGENCY_DESCRIPTION, {
      onError: setErrorLike,
    });

  const handleIncludeAgencyDescriptionToggle = useCallback(
    async (value: boolean) => {
      await updateIncludeAgencyDescription({
        variables: {
          id: cmaReportData?.id,
          include_agency_description: value,
        },
      });
    },
    [cmaReportData?.id, updateIncludeAgencyDescription],
  );

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <SettingsIcon
              size={20}
              color={'var(--mui-palette-text-secondary)'}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('Settings')}
            </Typography>
            {!expanded && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {includeAgencyDescription && ` • ${t('Show description')}`}
              </Typography>
            )}
          </Stack>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUpIcon size={20} />
            ) : (
              <ChevronDownIcon size={20} />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ p: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeAgencyDescription}
                  onChange={e =>
                    handleIncludeAgencyDescriptionToggle(e.target.checked)
                  }
                  disabled={loadingUpdate}
                />
              }
              label={t('Show agency description')}
              sx={{ m: 0 }}
            />
          </Box>
        </Collapse>
      </Box>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export default StepAgencyProfileSettings;
